import React from 'react';
import _ from 'lodash';
import { useSelector } from 'react-redux';
import { Redirect } from 'react-router-dom';
import { ppsiInProgressRoute } from '../routes';
import PpsiForm from '../components/PpsiForm';
import ProgressFullscreen from '../components/common/ProgressFullscreen';
import { getAvailableLanguages, getConfigs } from '../features/app/selectors';
import usePpisRelatedToFormulaCode from '../features/PpiContainer/hooks/usePpisRelatedToFormulaCode';
import useCreatePpsiFromGlobalNir from '../features/PpsiContainer/hooks/useCreatePpsiFromGlobalNir';
import useGlobalPpsi from '../features/PpsiContainer/hooks/useGlobalPpsi';
import useGlobalPpis from '../features/PpiContainer/hooks/useGlobalPpis';
import useUtensils from '../features/UtensilContainer/hooks/useUtensils';
import { toJS } from '../utils/immutableUtils';
import useDishes from '../features/DishContainer/hooks/useDishes';
import copyPpsi from '../features/PpsiContainer/copy';
import { getPpsiDefaultData } from '../features/PpsiContainer/util';

import useQueryParams from '../utils/useQueryParams';
import { MARKET_GLOBAL_LANG } from '../features/app/constants';
import { Mode } from '../components/PpsiForm/constants';
import View from './views';
import useProductAreas from 'features/PpsiContainer/hooks/useProductAreas';

function clearPresentationName(data) {
  const dataWithoutName = _.cloneDeep(data);
  _.keys(data.info).forEach(language => {
    delete dataWithoutName.info[language].presentationName;
  });
  return dataWithoutName;
}

const PpsiCreateFromGlobalNirView = () => {
  const [
    fromPpsiId,
    fromEnrichmentId,
    formulaCode,
    globalPiaArticleNumber
  ] = useQueryParams(
    'ppsiId',
    'enrichmentId',
    'formulaCode',
    'globalPiaArticleNumber'
  );
  // Fetch PPSI to copy from
  const [piaProductAreas] = useProductAreas();
  const [fromPpsiRaw, isFetchingPpsi, fetchPpsiError] = useGlobalPpsi(
    fromPpsiId,
    {
      forceFetch: true
    }
  );
  const [isFetchingPpisRelatedToFormula] = usePpisRelatedToFormulaCode(
    formulaCode
  );
  const [isFetchingDishes, dishes] = useDishes({ formulaCode });
  const [
    createPpsiFromGlobal,
    isSaving,
    createdPpsiId
  ] = useCreatePpsiFromGlobalNir();
  const languages = useSelector(getAvailableLanguages);
  const getConfigData = useSelector(getConfigs).toJS();

  // Initiate sales area code and stoe code
  const storeLists =
    getConfigData && getConfigData.stores.map(item => item.code);
  const salesAreas =
    getConfigData && getConfigData.salesPoint.map(item => item.code);

  // Initiate fetching of utensils
  useUtensils();
  useGlobalPpis();

  const fromPpsi = toJS(fromPpsiRaw);
  const toDish = toJS(dishes.first());

  if (createdPpsiId) {
    return <Redirect to={ppsiInProgressRoute(createdPpsiId)} />;
  }

  if (fetchPpsiError) {
    throw fetchPpsiError;
  }

  if (isFetchingPpsi || isFetchingPpisRelatedToFormula || isFetchingDishes) {
    return <ProgressFullscreen open />;
  }

  // Copy PPSI data
  let enrichmentData = copyPpsi(
    fromPpsi,
    fromEnrichmentId,
    MARKET_GLOBAL_LANG,
    languages
  );

  enrichmentData = clearPresentationName(enrichmentData);
  const defaultData = getPpsiDefaultData(languages, toDish);

  View.PpsiCreateFromGlobalNirView.visit(toDish);
  return (
    <PpsiForm
      data={{
        ...enrichmentData
      }}
      defaultData={defaultData}
      isSaving={isSaving}
      onSave={(data, callback) =>
        createPpsiFromGlobal(data, callback, globalPiaArticleNumber)
      }
      dataLabels={{
        title: (data, { language }) =>
          _.get(data, `info.${language}.presentationName`)
      }}
      mode={Mode.CREATE}
      globalData={fromPpsi}
      isGlobalReferenceHidden
    />
  );
};

export default PpsiCreateFromGlobalNirView;
